





















































































































































































































































import Vue from 'vue';
import store from '@/store';
import { OrgStructure, OrgUser, OrgPeerGroup } from '@/models/orgStructure';
import * as Utils from '@/utils';
import { WebRequestStatus, BaseOrganization, OrgRole } from '@/models';
import { OrgRoleFilter } from '@/orgRole';
import { Packet } from '@/models/packet';
import { GoogleClassroomCourse } from '@/api/auth/google';
import { batchResultMessage } from '@/peerGroupBatch';

import CogsDefinition from '@/components/widgets/CogsDefinition.vue';
import OrganizationLoadGuard from '@/components/OrganizationLoadGuard.vue';
import OrganizationPacketList from '@/components/OrganizationPacketList.vue';
import OrganizationPeerGroupClassroomImporter from '@/components/OrganizationPeerGroupClassroomImporter.vue';
import OrganizationPeerGroupCreator from '@/components/OrganizationPeerGroupCreator.vue';
import OrganizationUserList from '@/components/OrganizationUserList.vue';
import PeerGroupBatchDialog from '@/components/PeerGroupBatchDialog.vue';
import PeerGroupList from '@/components/PeerGroupList.vue';
import PersistableItem from '@/components/PersistableItem.vue';
import UnrulrFilter from '@/components/Filter.vue';

enum View {
  GROUPS,
  MEMBERS,
  GOALS
}

export default Vue.extend({
  components: {
    CogsDefinition,
    OrganizationLoadGuard,
    OrganizationPacketList,
    OrganizationPeerGroupCreator,
    OrganizationPeerGroupClassroomImporter,
    OrganizationUserList,
    PeerGroupBatchDialog,
    PeerGroupList,
    PersistableItem,
    UnrulrFilter
  },

  props: {
    organizationId: Number
  },

  data() {
    return {
      filter: '',
      groupCount: 0,
      memberCount: 0,
      goalCount: 0,
      view: View.GROUPS,
      // Owned here rather than by the member list, so that shift-enter in
      // the search field can create users in the selected role too.
      roleFilter: 'ALL' as OrgRoleFilter,
      tempCreateId: Utils.idGenerator.next(),
      // Open state for the dialogs behind the group action menus.
      isCreatingGroup: false,
      isImportingGroup: false,
      isManagingArchive: false,
      batchToast: {
        open: false,
        message: '',
        // The last bulk change, which Undo reverses.  Null once undone, or
        // when nothing succeeded.
        undo: null as { isActive: boolean; peerGroupIds: number[] } | null
      }
    };
  },

  computed: {
    org(): OrgStructure {
      return store.state.org.org;
    },
    organization(): BaseOrganization {
      return store.state.org.basic;
    },
    users(): [OrgUser, WebRequestStatus][] {
      return this.org.users
        .slice()
        .sort(Utils.sortUsers)
        .map(u => [u, store.getters.org.status.user(u.userId)]);
    },
    packets(): Packet[] {
      return this.org.packets.map(p => p.packet).sort(Utils.sortNamed);
    },
    peerGroups(): OrgPeerGroup[] {
      return this.org.peerGroups;
    },
    adminAccess(): boolean {
      return store.state.org.adminAccess;
    },
    organizerAccess(): boolean {
      return store.state.org.organizerAccess;
    },
    groupCreateEnabled(): boolean {
      return store.state.org.organizerAccess || store.state.org.adminAccess;
    },
    // Organizers add people from the groups they teach instead.
    memberCreateEnabled(): boolean {
      return store.state.org.adminAccess;
    },
    // New members join in the role the list is filtered to.
    newMemberRole(): OrgRole {
      return this.roleFilter === 'ALL' ? OrgRole.MEMBER : this.roleFilter;
    },
    isGroupsView(): boolean {
      return this.view === View.GROUPS;
    },
    isMembersView(): boolean {
      return this.view === View.MEMBERS;
    },
    isGoalsView(): boolean {
      return this.view === View.GOALS;
    },
    createStatus(): WebRequestStatus {
      return store.state.org.activeRequests.create.getStatus(this.tempCreateId);
    },

    placeholderText(): string {
      switch (this.view) {
        case View.GROUPS:
          return 'Type to find or create a group';
        case View.MEMBERS:
          return this.memberCreateEnabled
            ? 'Type to find or add a member'
            : 'Type to find a member';
        case View.GOALS:
          return 'Type to find cogs';
      }

      return '';
    },

    hintText(): string | undefined {
      switch (this.view) {
        case View.GROUPS:
          return undefined;
        case View.MEMBERS:
          return this.memberCreateEnabled
            ? '* Paste in a list of emails to add multiple users at once'
            : undefined;
        case View.GOALS:
          return undefined;
      }

      return undefined;
    }
  },

  methods: {
    updateFilter(value: string) {
      this.filter = value;
    },

    // Only the Members tab adds people; elsewhere shift-enter does nothing.
    onShiftEnter(email: string) {
      if (this.isMembersView) {
        this.createOrgUser(email);
      }
    },

    createOrgUser(email: string) {
      if (!this.memberCreateEnabled) {
        return;
      }

      store.dispatch.org.addNewUserToOrg({ email, role: this.newMemberRole });
    },

    async createOrgUsers(emails: string[]) {
      if (!this.memberCreateEnabled) {
        return;
      }

      // Read once, so changing the filter mid-batch can't split the roles.
      const role = this.newMemberRole;

      for (let i = 0; i < emails.length; i++) {
        await store.dispatch.org.addNewUserToOrg({
          email: emails[i],
          role,
          delay: 0
        });
      }
    },

    async createOrgGroup(group: {
      name: string;
      section: string;
      description: string;
    }) {
      const newGroupId = await store.dispatch.org.createPeerGroup({
        tempCreateId: this.tempCreateId,
        ...group
      });

      this.finishUpGroupCreation(newGroupId);
    },

    async importClassroomCourse({ course }: { course: GoogleClassroomCourse }) {
      const newGroupId = await store.dispatch.org.importClassroomCourse({
        tempCreateId: this.tempCreateId,
        course
      });

      this.finishUpGroupCreation(newGroupId);
    },

    finishUpGroupCreation(newGroupId: number) {
      if (newGroupId == -1) {
        return;
      }

      const orgId = this.$route.params['organizationId'] as string;

      this.$router.push({
        name: 'OrganizationPeerGroup',
        params: {
          organizationId: orgId,
          peerGroupId: `${newGroupId}`
        }
      });
    },

    onBatchDone({
      isActive,
      succeeded,
      failed
    }: {
      isActive: boolean;
      succeeded: number[];
      failed: number[];
    }) {
      this.showBatchToast(
        batchResultMessage(isActive, succeeded.length, failed.length),
        succeeded.length > 0 ? { isActive, peerGroupIds: succeeded } : null
      );
    },

    async undoBatch() {
      const undo = this.batchToast.undo;

      if (!undo) {
        return;
      }

      this.batchToast.open = false;
      this.batchToast.undo = null;

      const isActive = !undo.isActive;
      const {
        succeeded,
        failed
      } = await store.dispatch.org.setPeerGroupsActive({
        peerGroupIds: undo.peerGroupIds,
        isActive
      });

      this.showBatchToast(
        failed.length === 0
          ? 'Undone.'
          : batchResultMessage(isActive, succeeded.length, failed.length),
        null
      );
    },

    showBatchToast(
      message: string,
      undo: { isActive: boolean; peerGroupIds: number[] } | null
    ) {
      // Closing first restarts the timeout when a toast is already showing.
      this.batchToast.open = false;

      this.$nextTick(() => {
        this.batchToast = { open: true, message, undo };
      });
    },

    goToLtiRegistration() {
      this.$router.push({
        name: 'LtiRegistration',
        params: { organizationId: `${this.organizationId}` }
      });
    },

    buildGoals() {
      this.$router.push({
        name: 'OrganizationPacketCreator'
      });
    },

    toggleGroups() {
      this.setRoute(View.GROUPS);
    },
    toggleMembers() {
      this.setRoute(View.MEMBERS);
    },
    toggleGoals() {
      this.setRoute(View.GOALS);
    },

    setView(view: View) {
      this.view = view;
    },

    setRoute(view: View) {
      this.$router.push({
        name: 'Organization',
        params: { organizationId: `${this.organizationId}` },
        query: {
          view: `${view}`
        }
      });
    },

    handleGroupFilter({ count }: { count: number }) {
      this.groupCount = count;
    },
    handleMemberFilter({ count }: { count: number }) {
      this.memberCount = count;
    },
    handleGoalFilter({ count }: { count: number }) {
      this.goalCount = count;
    },

    parseViewFromRoute() {
      const routeView = +(this.$route.query.view || 0);

      if (routeView != this.view) {
        this.setView(routeView);
      }
    }
  },

  watch: {
    $route() {
      this.parseViewFromRoute();
    }
  },

  created() {
    this.parseViewFromRoute();
  }
});
